/**
 * Whether the standing desktop offer has been dismissed.
 *
 * The old invitation banner rendered only while `?invitation=accepted` was in
 * the URL, so it vanished on the invitee's first click and there was nothing
 * to dismiss. A standing offer needs the opposite: it survives navigation, so
 * it must be possible to turn off, and that has to outlive the tab or the
 * dismissal is meaningless.
 *
 * Deliberately per-origin rather than per-org. Someone who has said "I don't
 * want the app" has said it about the app, not about one team, and asking them
 * again when they switch orgs is the same nag with extra steps.
 *
 * Storage is injectable so the branch is testable without a DOM, and every
 * access is guarded: Safari throws on `localStorage` in some privacy modes and
 * an unreadable preference must not take the console down.
 */

interface OfferStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type OfferStorageProvider = () => OfferStorage;

const STORAGE_KEY = 'nimbalyst:web-console:desktop-offer-dismissed';

function browserStorage(): OfferStorage {
  return window.localStorage;
}

export function readDesktopOfferDismissed(
  getStorage: OfferStorageProvider = browserStorage,
): boolean {
  try {
    return getStorage().getItem(STORAGE_KEY) === 'true';
  } catch {
    // Unreadable storage means "not dismissed": showing an offer one extra
    // time is a smaller failure than hiding the only path to the app.
    return false;
  }
}

export function rememberDesktopOfferDismissed(
  dismissed: boolean,
  getStorage: OfferStorageProvider = browserStorage,
): void {
  try {
    getStorage().setItem(STORAGE_KEY, String(dismissed));
  } catch {
    // The in-session state still applies when site data is unavailable.
  }
}
